import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  Card,
  Typography,
  message,
  InputNumber,
  DatePicker,
  Space,
  Divider,
  Table,
  Modal,
  Tag,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import OdometerField from '../Shared/OdometerField';
import {
  confirmMileageDecrease,
  getMileageErrorMessage,
  parseMileageDecreaseConflict,
  MileageConfirmation,
} from '../../utils/mileageConfirm';
import dayjs from 'dayjs';
import { Customer, Vehicle, WorkOrder, WorkOrderPart, WorkOrderLabor } from '../../types';
import { assetLabel } from '../../utils/assetLabel';
import { customerApi, vehicleApi, workOrderApi } from '../../services/api';
import PageRefreshWarning from '../PageRefreshWarning';
import TechnicianSelect from '../Shared/TechnicianSelect';
import { socketService } from '../../services/socketService';
import { useGuardedEditor, writeStash, readStash } from '../../contexts/SessionGuardContext';
import { rehydrateStashedDates } from '../../session/stashDates';
import { reportSaveError } from '../../utils/saveErrorNotice';
import { useResolvedRequirements } from '../../hooks/useResolvedRequirements';

/** Work order form fields bound to a DatePicker — see session/stashDates.ts. */
const WO_STASH_DATE_FIELDS = ['estimated_completion'] as const;

/**
 * The address fields this block surfaces. Which of them BLOCK is not decided
 * here — that comes from the server's resolved policy. This constant only says
 * "these four are what the address block shows and edits".
 */
const WO_ADDRESS_FIELDS = ['address', 'city', 'state', 'zip'] as const;

/** "Fresno, CA 93701" from whichever parts exist. Never renders stray commas. */
const formatCityLine = (c?: Customer | null): string => {
  if (!c) return '';
  const cityState = [c.city?.trim(), c.state?.trim()].filter(Boolean).join(', ');
  return [cityState, c.zip?.trim()].filter(Boolean).join(' ');
};

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface WorkOrderFormProps {
  workOrder?: WorkOrder;
  onSuccess?: (workOrder: WorkOrder) => void;
  onCancel?: () => void;
  modal?: boolean;
}

const WorkOrderForm: React.FC<WorkOrderFormProps> = ({
  workOrder,
  onSuccess,
  onCancel,
  modal = false,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    workOrder?.customer_id || null
  );
  const [parts, setParts] = useState<WorkOrderPart[]>(workOrder?.parts_needed || []);
  const [laborItems, setLaborItems] = useState<WorkOrderLabor[]>(workOrder?.labor_items || []);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  const location = useLocation();

  // ── Customer service address (Plan 3 Task 3) ──
  // Staff could not see the address here at all, so a job could reach approval
  // with nowhere for the van to drive. Read-only by default, editable in place.
  const [addressForm] = Form.useForm();
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);

  // What the approval gate will refuse without — asked, never hardcoded.
  const approvalRequirements = useResolvedRequirements('approval');

  const selectedCustomer =
    customers.find((c) => c.id === selectedCustomerId) ?? null;

  /** Address fields the policy blocks on that this customer has not got. */
  const missingAddressBlockers = selectedCustomer
    ? WO_ADDRESS_FIELDS.filter(
        (f) =>
          approvalRequirements.blocking.includes(f) &&
          !String((selectedCustomer as Record<string, any>)[f] ?? '').trim(),
      )
    : [];

  /**
   * The dialog's starting values.
   *
   * NOT set with `addressForm.setFieldsValue()` before opening: the Modal is
   * `destroyOnClose`, so its Form is unmounted at that moment and the values are
   * discarded — the dialog opened BLANK over an address that was plainly visible
   * behind it, and staff correcting one line would have wiped the other three.
   * jsdom does NOT reproduce that (the test passed before the fix); only driving
   * the live form showed it. `initialValues` is read on the remount, so it works.
   */
  const addressInitialValues = {
    address: selectedCustomer?.address ?? '',
    address_line2: selectedCustomer?.address_line2 ?? '',
    city: selectedCustomer?.city ?? '',
    state: selectedCustomer?.state ?? '',
    zip: selectedCustomer?.zip ?? '',
  };

  const openAddressModal = () => setAddressModalOpen(true);

  const saveAddress = async () => {
    if (!selectedCustomer?.id) return;
    let values: any;
    try {
      values = await addressForm.validateFields();
    } catch {
      return;
    }
    setSavingAddress(true);
    try {
      // Send ONLY the address fields. `PUT /customers/:id` is a genuine partial
      // update (every key optional, ≥1 required), and echoing the whole customer
      // row back at it is rejected with 400: the row carries `country: null`,
      // `is_tax_exempt: 0`, `address_needs_review: 0` and a derived
      // `vehicle_count`, none of which satisfy updateCustomerSchema. Caught by
      // driving the live form — the merged payload passed every unit test.
      const patch = {
        address: values.address ?? '',
        address_line2: values.address_line2 ?? '',
        city: values.city ?? '',
        state: values.state ?? '',
        zip: values.zip ?? '',
      };
      const response = await customerApi.update(selectedCustomer.id, patch);
      const saved = response?.data ?? { ...selectedCustomer, ...patch };
      setCustomers((prev) => prev.map((c) => (c.id === saved.id ? { ...c, ...saved } : c)));
      setAddressModalOpen(false);
      message.success('Address updated');
    } catch (error) {
      message.error('Failed to update the address');
    } finally {
      setSavingAddress(false);
    }
  };

  // WP-08 — odometer capture. useWatch (not getFieldValue) so the field's own
  // hint re-renders as the user types; getFieldValue does not trigger a render.
  const watchedOdometer = Form.useWatch('intake_odometer', form) as number | undefined;
  const watchedVehicleId = Form.useWatch('vehicle_id', form) as number | undefined;
  const selectedVehicleMileage =
    vehicles.find((v) => v.id === watchedVehicleId)?.current_mileage ?? null;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isEditing = Boolean(workOrder);

  useEffect(() => {
    fetchCustomers();
    if (workOrder) {
      setSelectedCustomerId(workOrder.customer_id);
      form.setFieldsValue({
        ...workOrder,
        estimated_completion: workOrder.estimated_completion ? dayjs(workOrder.estimated_completion) : null,
      });
    }
  }, [workOrder, form]);

  useEffect(() => {
    if (selectedCustomerId) {
      fetchCustomerVehicles(selectedCustomerId);
    } else {
      setVehicles([]);
    }
  }, [selectedCustomerId]);

  // Socket listener — re-fetch when Chrome extension dispatches items (C3-T19)
  useEffect(() => {
    const workOrderId = workOrder?.id;
    if (!workOrderId) return;
    socketService.joinEntityChannel('work_order', workOrderId);
    // Notify Chrome extension of current entity target
    window.postMessage(
      { type: 'cnf_target', entity: { type: 'work_order', id: workOrderId } },
      window.location.origin
    );
    const unsubscribe = socketService.onDispatchItemsAdded(async (evt) => {
      if (evt.entity_type === 'work_order' && evt.entity_id === workOrderId) {
        message.success(`Imported ${evt.count} item${evt.count === 1 ? '' : 's'} from ${evt.source}`);
        // Re-fetch the work order so the new pending line items show up.
        // Notify parent via onSuccess so its local state stays in sync.
        try {
          const response = await workOrderApi.getById(workOrderId);
          onSuccess?.(response.data);
        } catch (err) {
          console.error('Failed to re-fetch work order after dispatch:', err);
        }
      }
    });
    return () => { unsubscribe(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrder?.id]);

  const fetchCustomers = async () => {
    try {
      const response = await customerApi.getAll();
      setCustomers(response.data);
    } catch (error) {
      message.error('Failed to fetch customers');
    }
  };

  const fetchCustomerVehicles = async (customerId: number) => {
    try {
      const response = await vehicleApi.getByCustomerId(customerId);
      setVehicles(response.data);
    } catch (error) {
      message.error('Failed to fetch vehicles');
    }
  };

  const calculateTotalAmount = () => {
    const partsTotal = parts.reduce((sum, part) => sum + part.total_price, 0);
    const laborTotal = laborItems.reduce((sum, labor) => sum + labor.total_price, 0);
    return partsTotal + laborTotal;
  };

  const generateWorkOrderNumber = () => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `WO${year}${month}${day}-${random}`;
  };

  const handleSubmit = async (
    values: any,
    mileageConfirmation?: MileageConfirmation
  ): Promise<boolean> => {
    try {
      setLoading(true);

      const totalAmount = calculateTotalAmount();
      const workOrderData: Partial<WorkOrder> = {
        ...values,
        parts_needed: parts,
        labor_items: laborItems,
        total_amount: totalAmount,
        wo_number: workOrder?.wo_number || generateWorkOrderNumber(),
        estimated_completion: values.estimated_completion?.toISOString(),
        // Present only on the retry after the user confirms a backwards reading.
        ...(mileageConfirmation ?? {}),
      };

      let savedWorkOrder: WorkOrder = workOrderData as WorkOrder;

      if (isEditing && workOrder?.id) {
        await workOrderApi.update(workOrder.id, workOrderData);
        message.success('Work order updated successfully', 3);
      } else {
        // @ts-ignore TS2345: workOrderData is Partial<WorkOrder> but API expects Omit<WorkOrder,'id'> — pre-existing type gap
        const created = await workOrderApi.create(workOrderData);
        message.success('Work order created successfully', 3);
        // Forward the server response (with real id/timestamps) when available,
        // falling back to the locally-built object only if it's missing.
        savedWorkOrder = (created.data as WorkOrder) || (workOrderData as WorkOrder);
        const prefix = location.pathname.startsWith('/owner/') ? '/owner'
                     : location.pathname.startsWith('/advisor/') ? '/advisor'
                     : '/admin';
        navigate(`${prefix}/work-orders/${created.data.id}`);
      }

      setHasUnsavedChanges(false);
      onSuccess?.(savedWorkOrder);
      return true;
    } catch (error) {
      // WP-08: a 409 means the odometer went backwards. That is allowed —
      // rollbacks and cluster swaps are real — but it has to be confirmed, so
      // ask instead of failing the save outright.
      const decrease = parseMileageDecreaseConflict(error);
      if (decrease && !mileageConfirmation) {
        setLoading(false);
        const confirmed = await confirmMileageDecrease(decrease);
        if (!confirmed) return false;
        return handleSubmit(values, confirmed);
      }

      // WP-03. Was an unconditional "Failed to save work order. Please try
      // again." — it did not even look at the response body, so a missing
      // customer address and a dropped connection produced the same sentence,
      // and "please try again" was actively wrong for the first.
      const mileageMessage = getMileageErrorMessage(error);
      if (mileageMessage) {
        message.error(mileageMessage, 5);
      } else {
        reportSaveError(message, error, {
          fallback: 'Failed to save work order',
          focusField: (name) => form.scrollToField(name, { behavior: 'smooth', block: 'center' }),
        });
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Tier B — register with session guard for graceful-exit server save + stash fallback.
  const woStashKey = (isEditing && workOrder?.id) ? `workorder-${workOrder.id}` : 'workorder-new';
  useGuardedEditor(woStashKey, {
    isDirty: () => hasUnsavedChanges,
    validateAndSave: async () => {
      try {
        const values = await form.validateFields();
        const ok = await handleSubmit(values);
        if (!ok) return { saved: false };
        return { saved: true, recordId: workOrder?.id };
      } catch {
        return { saved: false };
      }
    },
    stash: () => writeStash(woStashKey, { values: form.getFieldsValue(true), route: window.location.pathname }),
    // Same JSON round-trip problem as the estimate form: `estimated_completion`
    // is a DatePicker field and returns from the stash as a string.
    restore: () => {
      const v = readStash(woStashKey);
      if (!v?.values) return false;
      form.setFieldsValue(rehydrateStashedDates(v.values, WO_STASH_DATE_FIELDS));
      return true;
    },
  });

  const handleBack = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/admin/work-orders');
    }
  };

  const addPart = () => {
    const newPart: WorkOrderPart = {
      work_order_id: workOrder?.id || 0,
      part_name: '',
      quantity: 1,
      unit_price: 0,
      total_price: 0,
      status: 'needed',
    };
    setParts([...parts, newPart]);
    setHasUnsavedChanges(true);
  };

  const updatePart = (index: number, field: keyof WorkOrderPart, value: any) => {
    const updatedParts = [...parts];
    updatedParts[index] = { ...updatedParts[index], [field]: value };

    if (field === 'quantity' || field === 'unit_price') {
      updatedParts[index].total_price = updatedParts[index].quantity * updatedParts[index].unit_price;
    }

    setParts(updatedParts);
    setHasUnsavedChanges(true);
  };

  const removePart = (index: number) => {
    setParts(parts.filter((_, i) => i !== index));
    setHasUnsavedChanges(true);
  };

  const addLabor = () => {
    const newLabor: WorkOrderLabor = {
      work_order_id: workOrder?.id || 0,
      description: '',
      hours: 1,
      hourly_rate: 75,
      total_price: 75,
    };
    setLaborItems([...laborItems, newLabor]);
    setHasUnsavedChanges(true);
  };

  const updateLabor = (index: number, field: keyof WorkOrderLabor, value: any) => {
    const updatedLabor = [...laborItems];
    updatedLabor[index] = { ...updatedLabor[index], [field]: value };

    if (field === 'hours' || field === 'hourly_rate') {
      updatedLabor[index].total_price = updatedLabor[index].hours * updatedLabor[index].hourly_rate;
    }

    setLaborItems(updatedLabor);
    setHasUnsavedChanges(true);
  };

  const removeLabor = (index: number) => {
    setLaborItems(laborItems.filter((_, i) => i !== index));
    setHasUnsavedChanges(true);
  };

  const partsColumns = [
    {
      title: 'Part Name',
      dataIndex: 'part_name',
      key: 'part_name',
      render: (text: string, record: WorkOrderPart, index: number) => (
        <Input
          value={text}
          onChange={(e) => updatePart(index, 'part_name', e.target.value)}
          placeholder="Enter part name"
        />
      ),
    },
    {
      title: 'Part #',
      dataIndex: 'part_number',
      key: 'part_number',
      width: 120,
      render: (text: string, record: WorkOrderPart, index: number) => (
        <Input
          value={text}
          onChange={(e) => updatePart(index, 'part_number', e.target.value)}
          placeholder="Part #"
        />
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      render: (value: number, record: WorkOrderPart, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => updatePart(index, 'quantity', val || 0)}
          min={0}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 100,
      render: (value: number, record: WorkOrderPart, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => updatePart(index, 'unit_price', val || 0)}
          min={0}
          formatter={(value) => `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, ''))}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 100,
      render: (value: number) => `$${value.toFixed(2)}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string, record: WorkOrderPart, index: number) => (
        <Select
          value={status}
          onChange={(val) => updatePart(index, 'status', val)}
          style={{ width: '100%' }}
        >
          <Option value="needed">Needed</Option>
          <Option value="ordered">Ordered</Option>
          <Option value="received">Received</Option>
          <Option value="installed">Installed</Option>
        </Select>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 60,
      render: (text: any, record: WorkOrderPart, index: number) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => removePart(index)}
          danger
        />
      ),
    },
  ];

  const laborColumns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: WorkOrderLabor, index: number) => (
        <Input
          value={text}
          onChange={(e) => updateLabor(index, 'description', e.target.value)}
          placeholder="Labor description"
        />
      ),
    },
    {
      title: 'Hours',
      dataIndex: 'hours',
      key: 'hours',
      width: 100,
      render: (value: number, record: WorkOrderLabor, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => updateLabor(index, 'hours', val || 0)}
          min={0}
          step={0.5}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Rate',
      dataIndex: 'hourly_rate',
      key: 'hourly_rate',
      width: 100,
      render: (value: number, record: WorkOrderLabor, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => updateLabor(index, 'hourly_rate', val || 0)}
          min={0}
          formatter={(value) => `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, ''))}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 100,
      render: (value: number) => `$${value.toFixed(2)}`,
    },
    {
      title: 'Action',
      key: 'action',
      width: 60,
      render: (text: any, record: WorkOrderLabor, index: number) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => removeLabor(index)}
          danger
        />
      ),
    },
  ];

  return (
    <>
      <PageRefreshWarning enabled={hasUnsavedChanges && !loading} />
      <div className={modal ? '' : 'p-6 max-w-6xl mx-auto'}>
      {!modal && (
        <div className="flex items-center gap-4 mb-6">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-800"
          >
            Back
          </Button>
          <div>
            <Title level={2} className="mb-0">
              {isEditing ? 'Edit Work Order' : 'New Work Order'}
            </Title>
            <p className="text-gray-600">
              {isEditing ? 'Update work order details' : 'Create a new repair job'}
            </p>
          </div>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={() => setHasUnsavedChanges(true)}
        requiredMark={false}
        size="large"
      >
        <Row gutter={[24, 24]}>
          {/* Customer and Vehicle Selection */}
          <Col xs={24}>
            <Card title="Customer & Vehicle" className="mb-6">
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="customer_id"
                    label="Customer"
                    rules={[{ required: true, message: 'Please select a customer' }]}
                  >
                    <Select
                      placeholder="Select customer"
                      showSearch
                      optionFilterProp="children"
                      onChange={setSelectedCustomerId}
                      disabled={isEditing}
                    >
                      {customers.map((customer) => (
                        <Option key={customer.id} value={customer.id}>
                          {customer.company_name || `${customer.first_name} ${customer.last_name}`}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="vehicle_id"
                    label="Vehicle"
                    rules={[{ required: true, message: 'Please select a vehicle' }]}
                  >
                    <Select
                      placeholder="Select vehicle"
                      disabled={!selectedCustomerId || isEditing}
                    >
                      {vehicles.map((vehicle) => (
                        <Option key={vehicle.id} value={vehicle.id}>
                          {assetLabel(vehicle)}
                          {vehicle.asset_type !== 'equipment' && vehicle.license_plate ? ` (${vehicle.license_plate})` : ''}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* Service address — read-only with an inline edit. This is a mobile
                  shop: without it there is nowhere for the van to drive, and the
                  approval gate will refuse the job. */}
              {selectedCustomer && (
                <div
                  style={{
                    marginTop: 4,
                    padding: '12px',
                    background: '#fafafa',
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <EnvironmentOutlined style={{ color: '#8c8c8c', marginTop: 4 }} />
                  <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Text strong style={{ fontSize: 13 }}>Service address</Text>
                      {missingAddressBlockers.length > 0 && (
                        <Tag color="warning" style={{ margin: 0, fontSize: 11 }}>
                          Needed before approval
                        </Tag>
                      )}
                    </div>
                    {selectedCustomer.address?.trim() || formatCityLine(selectedCustomer) ? (
                      <div style={{ fontSize: 13, color: '#595959', wordBreak: 'break-word' }}>
                        {selectedCustomer.address?.trim() && <div>{selectedCustomer.address}</div>}
                        {selectedCustomer.address_line2?.trim() && (
                          <div>{selectedCustomer.address_line2}</div>
                        )}
                        {formatCityLine(selectedCustomer) && (
                          <div>{formatCityLine(selectedCustomer)}</div>
                        )}
                      </div>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        No address on file
                      </Text>
                    )}
                  </div>
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={openAddressModal}
                    style={{ minHeight: 44, paddingLeft: 0, paddingRight: 0 }}
                  >
                    Edit address
                  </Button>
                </div>
              )}
            </Card>
          </Col>

          {/* Work Order Details */}
          <Col xs={24}>
            <Card title="Work Order Details" className="mb-6">
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="status"
                    label="Status"
                    rules={[{ required: true }]}
                    initialValue="new"
                  >
                    <Select>
                      <Option value="new">New</Option>
                      <Option value="in_progress">In Progress</Option>
                      <Option value="waiting_parts">Waiting Parts</Option>
                      <Option value="waiting_approval">Waiting Approval</Option>
                      <Option value="completed">Completed</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="priority"
                    label="Priority"
                    rules={[{ required: true }]}
                    initialValue="normal"
                  >
                    <Select>
                      <Option value="low">Low</Option>
                      <Option value="normal">Normal</Option>
                      <Option value="high">High</Option>
                      <Option value="urgent">Urgent</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  {/* Staff picker persists the users.id FK (assigned_technician_id) —
                      the column owner P&L uses to cost labor — and mirrors the
                      display name into assigned_technician for legacy surfaces. */}
                  <Form.Item name="assigned_technician_id" label="Assigned Technician">
                    <TechnicianSelect
                      legacyName={workOrder?.assigned_technician}
                      placeholder="Select technician"
                      onChange={(_techId, techName) =>
                        form.setFieldsValue({ assigned_technician: techName })
                      }
                    />
                  </Form.Item>
                  {/* Hidden mirror keeps assigned_technician registered so it is
                      included in onFinish values alongside the FK. */}
                  <Form.Item name="assigned_technician" hidden>
                    <Input type="hidden" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item name="estimated_completion" label="Estimated Completion">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  {/* WP-08: ask for the odometer at intake. Optional by design —
                      it records the reading against the vehicle's service history
                      but never blocks creating the job. */}
                  <OdometerField
                    lastKnownMileage={selectedVehicleMileage}
                    value={watchedOdometer}
                  />
                </Col>
              </Row>

              <Form.Item
                name="problem_description"
                label="Problem Description"
                rules={[{ required: true, message: 'Please describe the problem' }]}
              >
                <TextArea
                  rows={4}
                  placeholder="Describe the customer's problem or requested service..."
                  showCount
                  maxLength={1000}
                />
              </Form.Item>

              <Form.Item name="work_performed" label="Work Performed">
                <TextArea
                  rows={4}
                  placeholder="Describe the work that was or will be performed..."
                  showCount
                  maxLength={1000}
                />
              </Form.Item>
            </Card>
          </Col>

          {/* Parts */}
          <Col xs={24}>
            <Card
              title="Parts"
              className="mb-6"
              extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={addPart} size={isMobile ? 'middle' : 'large'}>
                  Add Part
                </Button>
              }
            >
              {isMobile ? (
                parts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>No parts added yet</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {parts.map((part, index) => (
                      <Card key={index} size="small" style={{ backgroundColor: '#fafafa' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <Input
                            value={part.part_name}
                            onChange={(e) => updatePart(index, 'part_name', e.target.value)}
                            placeholder="Part name"
                            size="large"
                          />
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <Input
                              value={part.part_number}
                              onChange={(e) => updatePart(index, 'part_number', e.target.value)}
                              placeholder="Part #"
                            />
                            <Select
                              value={part.status}
                              onChange={(val) => updatePart(index, 'status', val)}
                              style={{ width: '100%' }}
                            >
                              <Option value="needed">Needed</Option>
                              <Option value="ordered">Ordered</Option>
                              <Option value="received">Received</Option>
                              <Option value="installed">Installed</Option>
                            </Select>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, alignItems: 'center' }}>
                            <InputNumber
                              value={part.quantity}
                              onChange={(val) => updatePart(index, 'quantity', val || 0)}
                              min={0}
                              style={{ width: '100%' }}
                              placeholder="Qty"
                            />
                            <InputNumber
                              value={part.unit_price}
                              onChange={(val) => updatePart(index, 'unit_price', val || 0)}
                              min={0}
                              formatter={(value) => `$${value}`}
                              parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, ''))}
                              style={{ width: '100%' }}
                              placeholder="Price"
                            />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ fontWeight: 600, color: '#22c55e' }}>${part.total_price.toFixed(2)}</span>
                              <Button type="text" icon={<DeleteOutlined />} onClick={() => removePart(index)} danger size="small" />
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )
              ) : (
                <Table
                  dataSource={parts}
                  columns={partsColumns}
                  pagination={false}
                  rowKey={(record, index) => index!}
                  locale={{ emptyText: 'No parts added yet' }}
                  scroll={{ x: 700 }}
                />
              )}
            </Card>
          </Col>

          {/* Labor */}
          <Col xs={24}>
            <Card
              title="Labor"
              className="mb-6"
              extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={addLabor} size={isMobile ? 'middle' : 'large'}>
                  Add Labor
                </Button>
              }
            >
              {isMobile ? (
                laborItems.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>No labor items added yet</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {laborItems.map((labor, index) => (
                      <Card key={index} size="small" style={{ backgroundColor: '#fafafa' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <Input
                            value={labor.description}
                            onChange={(e) => updateLabor(index, 'description', e.target.value)}
                            placeholder="Labor description"
                            size="large"
                          />
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, alignItems: 'center' }}>
                            <InputNumber
                              value={labor.hours}
                              onChange={(val) => updateLabor(index, 'hours', val || 0)}
                              min={0}
                              step={0.5}
                              style={{ width: '100%' }}
                              placeholder="Hours"
                            />
                            <InputNumber
                              value={labor.hourly_rate}
                              onChange={(val) => updateLabor(index, 'hourly_rate', val || 0)}
                              min={0}
                              formatter={(value) => `$${value}`}
                              parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, ''))}
                              style={{ width: '100%' }}
                              placeholder="Rate"
                            />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ fontWeight: 600, color: '#22c55e' }}>${labor.total_price.toFixed(2)}</span>
                              <Button type="text" icon={<DeleteOutlined />} onClick={() => removeLabor(index)} danger size="small" />
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )
              ) : (
                <Table
                  dataSource={laborItems}
                  columns={laborColumns}
                  pagination={false}
                  rowKey={(record, index) => index!}
                  locale={{ emptyText: 'No labor items added yet' }}
                  scroll={{ x: 600 }}
                />
              )}
            </Card>
          </Col>

          {/* Total */}
          <Col xs={24}>
            <Card className="mb-6">
              <div className="flex justify-between items-center text-xl font-semibold">
                <span>Total Amount:</span>
                <span className="text-green-600">
                  ${calculateTotalAmount().toFixed(2)}
                </span>
              </div>
            </Card>
          </Col>
        </Row>

        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'flex-end',
          gap: 12,
          paddingTop: 16,
          paddingBottom: isMobile ? 24 : 16,
          borderTop: '1px solid #f0f0f0',
        }}>
          <Button type="default" onClick={handleBack} size="large" disabled={loading} style={{ order: isMobile ? 2 : 1 }}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={loading}
            icon={<SaveOutlined />}
            size="large"
            style={{ fontWeight: 500, order: isMobile ? 1 : 2 }}
          >
            {loading ? 'Saving...' : isEditing ? 'Update Work Order' : 'Create Work Order'}
          </Button>
        </div>
      </Form>

      {/* Inline address fix — staff never has to leave the work order to add it. */}
      <Modal
        title="Service address"
        open={addressModalOpen}
        onCancel={() => setAddressModalOpen(false)}
        okText="Save address"
        confirmLoading={savingAddress}
        onOk={saveAddress}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          We come to you — this is where we'll meet the vehicle.
        </Text>
        <Form
          form={addressForm}
          layout="vertical"
          preserve={false}
          initialValues={addressInitialValues}
        >
          <Form.Item label="Street Address" name="address">
            <Input placeholder="123 Main Street" />
          </Form.Item>
          <Form.Item label="Apt / Suite" name="address_line2">
            <Input placeholder="Apt 4B" />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label="City" name="city">
                <Input placeholder="City" />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="State" name="state">
                <Input placeholder="CA" />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="ZIP Code" name="zip">
                <Input placeholder="12345" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
    </>
  );
};

export default WorkOrderForm;